/**
 * Calcula a distância de Levenshtein entre duas strings
 * Algoritmo de programação dinâmica para medir similaridade
 */
export function levenshteinDistance(str1: string, str2: string): number {
  const len1 = str1.length;
  const len2 = str2.length;
  const matrix: number[][] = [];

  // Inicializar matriz
  for (let i = 0; i <= len1; i++) {
    matrix[i] = [i];
  }
  for (let j = 0; j <= len2; j++) {
    matrix[0][j] = j;
  }

  // Calcular distâncias
  for (let i = 1; i <= len1; i++) {
    for (let j = 1; j <= len2; j++) {
      const cost = str1[i - 1] === str2[j - 1] ? 0 : 1;
      matrix[i][j] = Math.min(
        matrix[i - 1][j] + 1,      // Deleção
        matrix[i][j - 1] + 1,      // Inserção
        matrix[i - 1][j - 1] + cost // Substituição
      );
    }
  }

  return matrix[len1][len2];
}

/**
 * Calcula score de similaridade (0-1, onde 1 = match perfeito)
 */
export function fuzzyScore(search: string, target: string): number {
  const searchLower = search.toLowerCase().trim();
  const targetLower = target.toLowerCase().trim();

  // Match exato = score máximo
  if (targetLower === searchLower) return 1.0;

  // Substring exata = score alto
  if (targetLower.includes(searchLower)) return 0.9;

  // Calcular distância Levenshtein
  const distance = levenshteinDistance(searchLower, targetLower);
  const maxLength = Math.max(searchLower.length, targetLower.length);
  
  // Normalizar para 0-1 (quanto menor a distância, maior o score)
  const similarity = 1 - (distance / maxLength);
  
  return similarity;
}

export interface FuzzyMatch<T> {
  item: T;
  score: number;
  matches: boolean;
}

/**
 * Busca fuzzy em array de strings
 * @param searchTerm - Termo de busca
 * @param items - Array de strings para buscar
 * @param threshold - Score mínimo para considerar match (0-1)
 * @returns Array ordenado por relevância
 */
export function fuzzySearchStrings(
  searchTerm: string,
  items: string[],
  threshold: number = 0.6
): FuzzyMatch<string>[] {
  if (!searchTerm.trim()) {
    return items.map(item => ({ item, score: 1.0, matches: true }));
  }

  const results = items
    .map(item => ({
      item,
      score: fuzzyScore(searchTerm, item),
      matches: true
    }))
    .filter(result => result.score >= threshold)
    .sort((a, b) => b.score - a.score);

  return results;
}

/**
 * Busca fuzzy em objeto categorizado (ex: { "Saúde": ["Medicina", ...] })
 */
export function fuzzySearchCategorized<T extends Record<string, string[]>>(
  searchTerm: string,
  data: T,
  threshold: number = 0.6
): Partial<T> {
  if (!searchTerm.trim()) return data;

  const filtered: Partial<T> = {};

  Object.entries(data).forEach(([category, options]) => {
    const matches = fuzzySearchStrings(searchTerm, options, threshold);
    if (matches.length > 0) {
      filtered[category as keyof T] = matches.map(m => m.item) as T[keyof T];
    }
  });

  return filtered;
}
